import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/**
 * Alertes de stock (TF-072) : ruptures, articles sous le minimum et surstock, avec
 * recommandation et valeur immobilisée. Auto-suffisant (types inline + appel API direct).
 */
interface LigneAlerteStock {
  articleId: number;
  reference: string;
  designation: string;
  stockActuel: number;
  stockMinimum: number;
  statut: 'RUPTURE' | 'SOUS_MINIMUM' | 'SURSTOCK';
  delaiApprovisionnement: number | null;
  valeurStock: number;
  recommandation: string;
}

interface AlertesStockData {
  nbRupture: number;
  nbSousMinimum: number;
  nbSurstock: number;
  valeurSurstock: number;
  lignes: LigneAlerteStock[];
}

const STATUT_LABEL: Record<LigneAlerteStock['statut'], string> = {
  RUPTURE: 'Rupture',
  SOUS_MINIMUM: 'Sous minimum',
  SURSTOCK: 'Surstock',
};
const STATUT_CLASS: Record<LigneAlerteStock['statut'], string> = {
  RUPTURE: 'bg-red-100 text-red-700',
  SOUS_MINIMUM: 'bg-amber-100 text-amber-700',
  SURSTOCK: 'bg-blue-100 text-blue-700',
};

export function AlertesStock() {
  const [data, setData] = useState<AlertesStockData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<AlertesStockData>('/stock-alertes').then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!data) return <div className="card">Alertes indisponibles.</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Alertes stock</h1>
        <p className="page-subtitle">Ruptures, sous-minimum et surstock — avec recommandations</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card">
          <p className="text-xs text-gray-500">Ruptures</p>
          <p className="text-2xl font-bold text-red-600">{data.nbRupture}</p>
        </div>
        <div className="card">
          <p className="text-xs text-gray-500">Sous le minimum</p>
          <p className="text-2xl font-bold text-amber-600">{data.nbSousMinimum}</p>
        </div>
        <div className="card">
          <p className="text-xs text-gray-500">Surstock</p>
          <p className="text-2xl font-bold text-blue-600">{data.nbSurstock}</p>
        </div>
        <div className="card">
          <p className="text-xs text-gray-500">Valeur surstock</p>
          <MoneyDisplay amount={data.valeurSurstock} size="lg" className="font-bold" />
        </div>
      </div>

      {data.lignes.length === 0 ? (
        <div className="card text-center text-gray-500 py-10">Aucune alerte : votre stock est sain.</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-3 py-2">Article</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2 text-right">Stock</th>
                <th className="px-3 py-2 text-right">Minimum</th>
                <th className="px-3 py-2 text-right">Valeur</th>
                <th className="px-3 py-2">Recommandation</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.lignes.map((l) => (
                <tr key={l.articleId} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-800">{l.reference} — {l.designation}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUT_CLASS[l.statut]}`}>
                      {STATUT_LABEL[l.statut]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">{l.stockActuel}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{l.stockMinimum}</td>
                  <td className="px-3 py-2 text-right"><MoneyDisplay amount={l.valeurStock} size="sm" /></td>
                  <td className="px-3 py-2 text-gray-600">{l.recommandation}</td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/articles/${l.articleId}/edit`} className="text-blue-600 hover:underline whitespace-nowrap">Voir →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AlertesStock;
